import type { KyInstance } from "ky";
import { useEffect } from "react";
import { measureSince } from "./perfTiming";
import { runtimeSessionHeaders, runtimeSessionId } from "./runtimeSession";
import {
  capabilityCardsRequested,
  capabilityCardsUnavailable,
  publishCapabilityCards,
  resetCapabilityCardsForTest,
} from "./capabilities";
import { ANALYTICS_EVENTS, trackEvent } from "@repo/ui/lib/analytics";

/** What a warm-up carries besides the session: the same client-identity fields a turn sends. */
export interface WarmupClient {
  clientApp?: string;
  clientAppVersion?: string;
  clientAppDesktopVersion?: string;
}

/**
 * Generous on purpose: a cold warm-up builds tiger_agent's graph and waits for messaging to build
 * its own (MCP connect included), and nothing waits on this request.
 */
const WARMUP_TIMEOUT_MS = 45_000;

/**
 * Don't re-warm one session more often than this. Covers React StrictMode's double effect, a
 * provider remount, and tab-visibility flapping; well under AgentCore's default 15-minute idle
 * timeout, so a chat left open past it still gets re-warmed when the user comes back.
 */
export const REWARM_AFTER_MS = 5 * 60_000;

/**
 * When each session was last warmed. Cleared again when the probe or the warm-up fails, so a
 * transient error doesn't block the next attempt for a whole window; kept when the server just
 * doesn't advertise `"warmup"`, so an older build isn't re-probed on every visibility change.
 */
const lastWarmedAt = new Map<string, number>();

/**
 * Per session, whether its latest answered capabilities probe listed `"warmup"` in `features`.
 * That is the same server signal the warm-up is gated on, and it also means the server refuses a
 * runtime session to any user but the one who started it — which is what makes pinning a chat
 * turn safe. Absent (no probe has answered yet) and `false` (the latest answer didn't list it)
 * both mean turns go unpinned. A probe that fails says nothing about the server, so it leaves the
 * entry as it was: a network blip on a re-warm doesn't unpin a chat that was already pinned, and
 * a real rollback still unpins it, because that server answers without `"warmup"`. See
 * `canPinTurns`.
 */
const pinningConfirmed = new Map<string, boolean>();

/**
 * Sessions whose pinned capabilities probe is in flight. While AgentCore is still provisioning a
 * session's microVM, a second request to the same session can fail (a 409, or a dropped
 * connection the client can't safely retry), so a chat turn waits for the probe to settle
 * first — see `whenSessionReady`. That costs the turn nothing: on a cold session it would be
 * waiting for the same boot anyway.
 */
const provisioning = new Map<string, Promise<void>>();

/** Upper bound on how long a turn waits for its session's probe before sending anyway. */
export const SESSION_READY_MAX_WAIT_MS = 20_000;

/**
 * Resolves once `sessionId`'s in-flight warm-up probe (if any) has settled, after
 * `SESSION_READY_MAX_WAIT_MS`, or as soon as `signal` aborts, whichever is first. Never rejects:
 * an aborted turn resolves straight away and its own request then surfaces the abort. Immediate
 * when no probe is in flight, so a turn on a warm or never-warmed session isn't delayed.
 */
export function whenSessionReady(
  sessionId: string,
  signal?: AbortSignal,
  maxWaitMs: number = SESSION_READY_MAX_WAIT_MS,
): Promise<void> {
  const pending = provisioning.get(sessionId);
  if (!pending || signal?.aborted) return Promise.resolve();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;
  return Promise.race([
    pending,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, maxWaitMs);
      onAbort = () => resolve();
      signal?.addEventListener("abort", onAbort, { once: true });
    }),
  ]).finally(() => {
    clearTimeout(timer);
    if (onAbort) signal?.removeEventListener("abort", onAbort);
  });
}

/**
 * Whether a chat turn on `sessionId` may carry the runtime-session header: only once that
 * session's pinned capabilities probe has come back listing `"warmup"`. Read it after
 * `whenSessionReady`, so a probe that is still in flight gets its chance to confirm. Anything
 * else — no answered probe yet, one still running past the wait cap, or a server whose latest
 * answer doesn't advertise it — sends the turn unpinned, on a fresh microVM, as before pinning
 * existed.
 */
export function canPinTurns(sessionId: string): boolean {
  return pinningConfirmed.get(sessionId) === true;
}

/**
 * Test hook: forget every session's last warm-up, in-flight probe and pinning confirmation, and
 * the welcome cards the probes delivered.
 */
export function resetWarmupsForTest(): void {
  lastWarmedAt.clear();
  provisioning.clear();
  pinningConfirmed.clear();
  resetCapabilityCardsForTest();
}

/** Feature flag a server lists in its capabilities response when it understands `action: "warmup"`. */
const WARMUP_FEATURE = "warmup";

/** Non-streaming capabilities payload: `features` for the version gate, `cards` for the welcome screen. */
interface CapabilitiesPayload {
  features?: unknown;
  cards?: unknown;
}

/** How a capabilities probe ended: listed `"warmup"`, answered without it, or never answered. */
type ProbeOutcome = "supported" | "unsupported" | "failed";

/**
 * How a warm-up attempt that sent a probe ended, as reported to analytics. `probe_failed` leaves
 * the chat's turns unpinned; `warmup_failed` (the probe confirmed support, the warm-up post then
 * failed) still pins them.
 */
type WarmupOutcome = "warmed" | "unsupported" | "probe_failed" | "warmup_failed";

/**
 * Report one warm-up attempt. Deliberately low-cardinality: the outcome only — never the session
 * id or any error text.
 */
function trackWarmup(outcome: WarmupOutcome): void {
  trackEvent(ANALYTICS_EVENTS.sessionWarmup, { outcome });
}

/**
 * Warm `sessionId`'s runtime session in two pinned steps:
 *
 * 1. `{action: "capabilities"}` — answered by every agent build before its graph, spend gate and
 *    session store, so it is safe against any server version. Pinned, it also boots the microVM
 *    (and Python process) the chat's turns will use once they are pinned. Its answer is recorded
 *    per session: turns are pinned only when it lists `"warmup"` (see `canPinTurns`). Its cards
 *    become the welcome screen's (see `useCapabilityCards`).
 * 2. `{action: "warmup"}`, only if step 1 lists `"warmup"` in `features`. The agent builds its
 *    graph, primes the caller's spend-limit cache and warms the sub-agents behind the welcome
 *    cards (messaging) — no model call, no session/history entry, no credits (see
 *    agent-delivery-fx `AgentAPI._warmup`). An older build doesn't know the action and would run
 *    an empty turn on the user's session, which is why it is never sent one.
 *
 * Resolves `true` only when the full warm-up went out; `false` when nothing was sent (warmed
 * recently, can't be pinned), the server doesn't support it, or a request failed. Never rejects,
 * and nothing in the UI should await it. Every attempt that sends a probe reports one
 * "Session Warmup" analytics event with its outcome; the skipped ones report nothing.
 */
export async function warmSession(
  api: KyInstance,
  sessionId: string,
  client: WarmupClient = {},
  now: number = Date.now(),
): Promise<boolean> {
  // Unpinned, a warm-up would land on a microVM no turn will ever reuse. No probe means no cards
  // are coming for this chat, so the welcome screen stops loading.
  if (!runtimeSessionId(sessionId)) {
    capabilityCardsUnavailable();
    return false;
  }
  const last = lastWarmedAt.get(sessionId);
  if (last !== undefined && now - last < REWARM_AFTER_MS) return false;
  lastWarmedAt.set(sessionId, now);
  // A failed attempt shouldn't hold off the next one for a whole window.
  const forgetAttempt = () => {
    if (lastWarmedAt.get(sessionId) === now) lastWarmedAt.delete(sessionId);
  };
  const started = performance.now();
  const pinned = {
    timeout: WARMUP_TIMEOUT_MS,
    // Not retried: a 409 here means AgentCore is already provisioning this session, so it is
    // warming anyway. The probe then counts as failed, which leaves the chat's pinning as it was.
    retry: 0,
    headers: runtimeSessionHeaders(sessionId),
  };
  const clientFields = {
    ...(client.clientApp ? { client_app: client.clientApp } : {}),
    ...(client.clientAppVersion ? { client_app_version: client.clientAppVersion } : {}),
    ...(client.clientAppDesktopVersion
      ? { client_app_desktop_version: client.clientAppDesktopVersion }
      : {}),
  };
  try {
    // Starts the welcome screen's loading cap now that a probe is actually going out.
    const cardsRequestedIn = capabilityCardsRequested();
    const probe = api
      .post("invocations", {
        ...pinned,
        json: { action: "capabilities", stream: false, session_id: sessionId, ...clientFields },
      })
      .json<CapabilitiesPayload>();
    // Never rejects. Records whether turns may be pinned before it settles, so a turn that
    // waited on `whenSessionReady` reads this probe's answer, not a stale one.
    const outcome = probe.then(
      (capabilities): ProbeOutcome => {
        // The welcome screen's cards ride on this probe, so it needs no unpinned request (and
        // microVM) of its own.
        publishCapabilityCards(capabilities?.cards, cardsRequestedIn);
        const features = Array.isArray(capabilities?.features) ? capabilities.features : [];
        return features.includes(WARMUP_FEATURE) ? "supported" : "unsupported";
      },
      (): ProbeOutcome => {
        // No answer to take cards from: the welcome screen stops loading and shows the defaults.
        capabilityCardsUnavailable(cardsRequestedIn);
        return "failed";
      },
    );
    const settled = outcome.then((result) => {
      if (result !== "failed") pinningConfirmed.set(sessionId, result === "supported");
    });
    provisioning.set(sessionId, settled);
    void settled.then(() => {
      if (provisioning.get(sessionId) === settled) provisioning.delete(sessionId);
    });
    const result = await outcome;
    if (result === "failed") {
      forgetAttempt();
      trackWarmup("probe_failed");
      return false;
    }
    if (result === "unsupported") {
      trackWarmup("unsupported");
      return false;
    }
    await api.post("invocations", {
      ...pinned,
      json: { action: "warmup", stream: false, session_id: sessionId, ...clientFields },
    });
    measureSince("ask-tiger:warmup", started);
    trackWarmup("warmed");
    return true;
  } catch {
    // Best effort: a failed warm-up only means the first turn pays the cold start.
    forgetAttempt();
    trackWarmup("warmup_failed");
    return false;
  }
}

/**
 * Warm the chat's session as soon as it exists. `AgentProvider` mounts when the Ask Tiger panel
 * opens and remounts (with a fresh session id) on New chat or a history switch, so this covers all
 * three — always before the first message, since the id is minted at mount. Also re-warms when the
 * document becomes visible again, for a host that keeps the iframe alive while the panel is closed.
 */
export function useSessionWarmup(api: KyInstance, sessionId: string, client: WarmupClient): void {
  const { clientApp, clientAppVersion, clientAppDesktopVersion } = client;
  useEffect(() => {
    const warm = () =>
      void warmSession(api, sessionId, { clientApp, clientAppVersion, clientAppDesktopVersion });
    warm();
    const onVisibility = () => {
      if (document.visibilityState === "visible") warm();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [api, sessionId, clientApp, clientAppVersion, clientAppDesktopVersion]);
}
